import React from 'react'

export type ProductScreenKey =
  | 'network-dashboard'
  | 'network-relationships'
  | 'jobs-orders'
  | 'schedule-board'
  | 'worker-profile'
  | 'geofence-map'
  | 'timesheet-approvals'
  | 'payroll-export'
  | 'sales-pipeline'
  | 'worker-mobile'
  | 'worker-documents'

type ProductRow = {
  label: string
  value: string
  status: 'good' | 'warn' | 'info'
}

type ProductVisual = {
  title: string
  subtitle: string
  nav: string[]
  metrics: Array<{ label: string; value: string }>
  rows: ProductRow[]
  sideTitle: string
  sideItems: string[]
  cta: string
}

export const productScreenVisuals: Record<ProductScreenKey, ProductVisual> = {
  'network-dashboard': {
    title: 'Admin Dashboard',
    subtitle: 'Network overview for staffing operations',
    nav: ['Dashboard', 'Organizations', 'Workers', 'Jobs', 'Timesheets'],
    metrics: [
      { label: 'Active workers', value: '87' },
      { label: 'Open shifts', value: '14' },
      { label: 'Pending approvals', value: '12' },
    ],
    rows: [
      { label: 'West Bay Tower', value: '18 on site / 2 late', status: 'warn' },
      { label: 'Marina Security', value: 'All posts covered', status: 'good' },
      { label: 'Payroll export', value: '32 rows ready', status: 'info' },
    ],
    sideTitle: 'Today',
    sideItems: ['Agency + company approvals', 'Live attendance exceptions', 'Export readiness by location'],
    cta: 'Review operations',
  },
  'network-relationships': {
    title: 'Network Relationships',
    subtitle: 'Partner configuration and granted locations',
    nav: ['Organizations', 'Org Tree', 'Sharing', 'Roles'],
    metrics: [
      { label: 'Agencies', value: '6' },
      { label: 'Client companies', value: '9' },
      { label: 'Granted sites', value: '24' },
    ],
    rows: [
      { label: 'Doha Manpower -> West Bay Tower', value: 'Workers, jobs, timesheets', status: 'good' },
      { label: 'Gulf Guarding -> Marina Security', value: 'Location supervisor access', status: 'good' },
      { label: 'Facilities Partner -> North Mall', value: 'Documents required', status: 'warn' },
    ],
    sideTitle: 'Access scope',
    sideItems: ['Tenant boundaries preserved', 'Organization-owned worker records', 'Location grants and role checks'],
    cta: 'Configure sharing',
  },
  'jobs-orders': {
    title: 'Jobs / Orders',
    subtitle: 'Open demand, assigned workers, and match diagnostics',
    nav: ['Jobs', 'Schedule', 'Workers', 'Matches'],
    metrics: [
      { label: 'Open jobs', value: '11' },
      { label: 'Assigned', value: '87' },
      { label: 'Applications', value: '19' },
    ],
    rows: [
      { label: 'Concrete pour crew', value: '8 of 10 filled', status: 'warn' },
      { label: 'Security night post', value: '4 guards confirmed', status: 'good' },
      { label: 'Facility deep clean', value: '3 applicants to review', status: 'info' },
    ],
    sideTitle: 'Match signals',
    sideItems: ['Skills and availability', 'Document readiness', 'Pay and bill rate context'],
    cta: 'Assign workers',
  },
  'schedule-board': {
    title: 'Schedule Board',
    subtitle: 'Calendar view of shifts, coverage, and applicants',
    nav: ['Schedule', 'Calendar', 'Applicants', 'Coverage'],
    metrics: [
      { label: 'This week', value: '146 shifts' },
      { label: 'Unfilled', value: '7' },
      { label: 'Late risk', value: '3' },
    ],
    rows: [
      { label: 'Mon 07:00 - West Bay Tower', value: '12 assigned', status: 'good' },
      { label: 'Tue 19:00 - Marina Security', value: '1 replacement needed', status: 'warn' },
      { label: 'Wed 08:00 - North Mall', value: 'Applicants waiting', status: 'info' },
    ],
    sideTitle: 'Coverage',
    sideItems: ['Open shifts and assignments', 'Worker availability', 'Supervisor handoff'],
    cta: 'Open calendar',
  },
  'worker-profile': {
    title: 'Worker Profile',
    subtitle: 'Profile drawer with assignment and reliability context',
    nav: ['Workers', 'Profile', 'Skills', 'History'],
    metrics: [
      { label: 'Reliability', value: '94%' },
      { label: 'Completed', value: '38' },
      { label: 'Documents', value: '5/6' },
    ],
    rows: [
      { label: 'Ahmed Khalid', value: 'Forklift, security induction', status: 'good' },
      { label: 'Next assignment', value: 'West Bay Tower / 07:00', status: 'info' },
      { label: 'Work permit', value: 'Expires in 21 days', status: 'warn' },
    ],
    sideTitle: 'Readiness',
    sideItems: ['Skills and documents', 'Attendance history', 'Emergency/contact details'],
    cta: 'Review profile',
  },
  'geofence-map': {
    title: 'Locations & Geofences',
    subtitle: 'Live map, site radius, and punch exceptions',
    nav: ['Supervisor', 'Map', 'Locations', 'Geofences'],
    metrics: [
      { label: 'On site', value: '18' },
      { label: 'Late', value: '2' },
      { label: 'Warnings', value: '1' },
    ],
    rows: [
      { label: 'West Bay Tower', value: '350m geofence active', status: 'good' },
      { label: 'Gate B breadcrumb', value: 'Worker outside radius', status: 'warn' },
      { label: 'Offline punch', value: 'Queued for review', status: 'info' },
    ],
    sideTitle: 'Map context',
    sideItems: ['Location-scoped supervisors', 'Breadcrumb and punch trail', 'Geofence exception review'],
    cta: 'Open live map',
  },
  'timesheet-approvals': {
    title: 'Timesheets',
    subtitle: 'Approve disputed hours before payroll export',
    nav: ['Timesheets', 'Approvals', 'Disputes', 'Audit'],
    metrics: [
      { label: 'Pending', value: '12' },
      { label: 'Disputed', value: '3' },
      { label: 'Approved', value: '64' },
    ],
    rows: [
      { label: 'M. Rahman - Shift 1042', value: 'Break adjustment requested', status: 'warn' },
      { label: 'S. Bello - Shift 1045', value: 'Supervisor approved', status: 'good' },
      { label: 'Export batch', value: 'Awaiting company approval', status: 'info' },
    ],
    sideTitle: 'Review flow',
    sideItems: ['Punch, break, and job context', 'Comments and dispute history', 'Company/supervisor approvals'],
    cta: 'Approve rows',
  },
  'payroll-export': {
    title: 'Payroll Export',
    subtitle: 'Payroll-ready rows before settlement',
    nav: ['Payroll', 'Exports', 'Billing', 'Settlement'],
    metrics: [
      { label: 'Ready rows', value: '32' },
      { label: 'Gross hours', value: '412.5' },
      { label: 'Exports', value: '4' },
    ],
    rows: [
      { label: 'June payroll batch', value: 'CSV / XLSX / PDF ready', status: 'good' },
      { label: 'Client billing check', value: '2 invoice questions', status: 'warn' },
      { label: 'Settlement review', value: 'SAR + USD supported in demo', status: 'info' },
    ],
    sideTitle: 'Controls',
    sideItems: ['Date, worker, job, and location filters', 'Approval and export-state filters', 'Export audit history'],
    cta: 'Generate export',
  },
  'sales-pipeline': {
    title: 'Sales Workspace',
    subtitle: 'CRM pipeline, referral links, and commission tracking',
    nav: ['Sales Overview', 'Lead Pipeline', 'Referrals', 'Payouts'],
    metrics: [
      { label: 'Qualified leads', value: '8' },
      { label: 'Weighted ARR', value: '$128k' },
      { label: 'Pending commission', value: '$1,087.50' },
    ],
    rows: [
      { label: 'Lead: Gulf Facilities', value: 'Demo scheduled', status: 'info' },
      { label: 'Referral conversion', value: 'Paid invoice linked', status: 'good' },
      { label: 'Commission status', value: 'Pending approval', status: 'warn' },
    ],
    sideTitle: 'Revenue flow',
    sideItems: ['Source and campaign attribution', 'Status pipeline with owners', 'Paid-invoice commission basis'],
    cta: 'Review pipeline',
  },
  'worker-mobile': {
    title: 'Worker Mobile',
    subtitle: 'Assignment, geofence readiness, punch, and break flow',
    nav: ['Home', 'Jobs', 'Availability', 'Profile'],
    metrics: [
      { label: 'Next shift', value: '07:00' },
      { label: 'Distance', value: '120m' },
      { label: 'Status', value: 'Ready' },
    ],
    rows: [
      { label: 'West Bay Tower', value: 'Concrete pour crew', status: 'info' },
      { label: 'Geofence', value: 'Ready to punch', status: 'good' },
      { label: 'Timesheet', value: 'Submitted for review', status: 'warn' },
    ],
    sideTitle: 'Worker actions',
    sideItems: ['Punch in/out', 'Start or end break', 'View assignment details'],
    cta: 'Punch in',
  },
  'worker-documents': {
    title: 'Worker Documents',
    subtitle: 'Compliance documents and verification status',
    nav: ['Workers', 'Documents', 'Compliance', 'Assignments'],
    metrics: [
      { label: 'Verified', value: '71' },
      { label: 'Expiring', value: '9' },
      { label: 'Blocked', value: '2' },
    ],
    rows: [
      { label: 'Iqama / ID', value: 'Verified', status: 'good' },
      { label: 'Safety induction', value: 'Expires in 14 days', status: 'warn' },
      { label: 'Site access card', value: 'Required for job 984', status: 'info' },
    ],
    sideTitle: 'Compliance',
    sideItems: ['Expiry visibility', 'Assignment eligibility', 'Document audit context'],
    cta: 'Verify documents',
  },
}

type ProductScreenVisualProps = {
  variant: ProductScreenKey
  compact?: boolean
  className?: string
}

export const ProductScreenVisual: React.FC<ProductScreenVisualProps> = ({
  variant,
  compact = false,
  className = '',
}) => {
  const screen = productScreenVisuals[variant]

  return (
    <div className={`product-screen product-screen--${variant} ${compact ? 'product-screen--compact' : ''} ${className}`.trim()}>
      <div className="product-screen__topbar">
        <strong>NexusWorkforce360</strong>
        <span>{screen.title}</span>
      </div>
      <div className="product-screen__body">
        <aside className="product-screen__nav" aria-label={`${screen.title} navigation`}>
          {screen.nav.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </aside>
        <main className="product-screen__main">
          <div className="product-screen__heading">
            <div>
              <h3>{screen.title}</h3>
              <p>{screen.subtitle}</p>
            </div>
            <button type="button">{screen.cta}</button>
          </div>
          <div className="product-screen__metrics">
            {screen.metrics.map((metric) => (
              <div key={metric.label}>
                <strong>{metric.value}</strong>
                <span>{metric.label}</span>
              </div>
            ))}
          </div>
          <div className="product-screen__workspace">
            <section className="product-screen__table" aria-label={`${screen.title} rows`}>
              {screen.rows.map((row) => (
                <div className="product-screen__row" key={row.label}>
                  <span className={`product-screen__status product-screen__status--${row.status}`} />
                  <strong>{row.label}</strong>
                  <span>{row.value}</span>
                </div>
              ))}
            </section>
            <section className="product-screen__side">
              <h4>{screen.sideTitle}</h4>
              {screen.sideItems.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </section>
          </div>
        </main>
      </div>
    </div>
  )
}
